/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {PrimaryButton} from '../../components/buttons/PrimaryButton';
import {ButtonSpinner} from '../../components/loading/ButtonSpinner';
import {DLSTheme} from '../../types/types';

// defines the save button prop type definitions ...
export interface SaveButtonProps {
  onSave: (item: GenericObject) => void;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  styles?: any;
  overrideClasses?: GenericObject;
  autoWidth?: boolean;
};

// defines the save button styles ...
const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    width                          : (autoWidth) => !autoWidth ? '191px' : 'auto',
    marginRight                    : (autoWidth) => !autoWidth ? '28px' : '4px',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  }
}));

/**
 * @SaveButton
 *
 * The SaveButton provides generalized DLS Save Button which is typically used within
 * the various Entity Editors.
 *
 * ```typescript
 *
 *    import {SaveButton} from '@ion/dls/src/components/buttons/';
 *
 *    <SaveButton onSave={application}
 *      loading={loading}
 *      disabled={loading} />
 *
 * ```
 *
 * @param {Function} onSave callback which is invoked when the user clicks save.
 * @param {boolean}  loading When true, the loading indicator will be displayed.
 * @param {boolean}  disabled When true, the button will be disabled.
 */
export const SaveButton = ({
  onSave,
  overrideClasses,
  label=translate('common:editors.shared.done'),
  loading = false,
  disabled = false,
  autoWidth = false
}: SaveButtonProps) => {
  const classes = useStyles(autoWidth);

  return (
    <div className={classnames(classes.container, overrideClasses?.container)}>
      <PrimaryButton fullWidth={!autoWidth} type='submit' disabled={disabled} onClick={onSave}>
        {loading ? <ButtonSpinner /> : <>{label}</>}
      </PrimaryButton>
    </div>
  );
};

export default SaveButton;
